/*
 * Ryan O'Dowd
 * 2023-01-21
 * © Copyright 2023 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import React, {
  useState,
} from 'react';
import {
  TouchableOpacity,
  View,
} from 'react-native';
import {
  addI18nNote,
  updateI18nValue,
} from '@utils/store/actions';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import Highlighter from 'react-highlight-words';
import IconButton from '@components/common/IconButton';
import Note from './Note';
import Text from '@components/common/Text';
import TextInput from '@components/common/TextInput';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const Translation = (props: TranslationProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles);
  const dispatch = hooks.useTypedDispatch();

  const i18nObjectsForThisKey = hooks.useTypedSelector((state) => {
    return state.i18n[props.locale.id]?.i18n?.filter((i) => i.key === props.i18nKey) || [];
  });

  const [_isNotesOpen, setIsNotesOpen] = useState(false);
  let notes = hooks.useTypedSelector((state) => {
    return i18nObjectsForThisKey.flatMap((i) => {
      return state.i18nNotes[i.id] || [];
    });
  });

  i18nObjectsForThisKey.forEach((i) => {
    notes.push({
      id: `i18n_change_${i.id}`,
      created_at: i.created_at,
      updated_at: i.updated_at,
      user_id: globals.wrinklyBotId,
      i18n_id: i.id,
      upvoted: null,
      parent_note_id: null,
      notes: i.value,
    });
  });
  notes = notes.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  const [_newNoteText, setNewNoteText] = useState('');
  const [_updatedTextValue, setUpdatedTextValue] = useState('');
  const [_isEditable, setIsEditable] = useState(false);

  const isAdmin = hooks.useIsAdmin();

  const _saveNote = () => {
    dispatch(addI18nNote(props.i18nId, null, _newNoteText));
    setNewNoteText('');
  };

  const thumbUpIcon = [0, 1].includes(props.mostRecentVote) ? 'thumb-up' : 'thumb-up-outline';
  const thumbUpIconColor = props.mostRecentVote === 1 ? styles.colors.accentDark : (props.mostRecentVote === 0 ? styles.colors.lightGray : styles.colors.gray);

  const thumbDownIcon = [0, 1].includes(props.mostRecentVote) ? 'thumb-down' : 'thumb-down-outline';
  const thumbDownIconColor = props.mostRecentVote === 0 ? styles.colors.incorrect : (props.mostRecentVote === 1 ? styles.colors.lightGray : styles.colors.gray);

  return (
    <View
      style={[
        styles.container,
        props.mostRecentVote === 1 && styles.upvotedContainer,
        props.mostRecentVote === 0 && styles.downvotedContainer,
        props.isGeneralFeedback && styles.generalFeedbackContainer,
      ]}
    >
      <View style={styles.aboveFold}>
        <TouchableOpacity
          style={styles.translations}
          onPress={() => setIsNotesOpen(!_isNotesOpen)}
        >
          {props.isGeneralFeedback
            ? (
              <React.Fragment>
                <View style={styles.translationRow}>
                  <Highlighter
                    style={styles.generalFeedbackTitle}
                    highlightStyle={styles.highlightedText}
                    searchWords={props.searchTerms}
                    autoEscape={true}// @TODO: use this?
                    textToHighlight={props.englishValue}
                    caseSensitive={false}
                  />
                </View>
                <View style={styles.translationRow}>
                  <Highlighter
                    highlightStyle={styles.highlightedText}
                    searchWords={props.searchTerms}
                    autoEscape={true}// @TODO: use this?
                    textToHighlight={"Have notes that don't apply to a single word/phrase below?  Additional feedback can be provided here."}// @TODO: i18n
                    caseSensitive={false}
                  />
                </View>
              </React.Fragment>
            )
            : (
              <React.Fragment>
                {isAdmin &&
                  <View style={styles.translationRow}>
                    <Text style={styles.locale}>key:</Text>
                    <Highlighter
                      highlightStyle={styles.highlightedText}
                      searchWords={props.searchTerms}
                      autoEscape={true}// @TODO: use this?
                      textToHighlight={props.i18nKey}
                      caseSensitive={false}
                    />
                  </View>
                }
                <View style={styles.translationRow}>
                  <Text style={styles.locale}>eng:</Text>
                  <Highlighter
                    highlightStyle={styles.highlightedText}
                    searchWords={props.searchTerms}
                    autoEscape={true}// @TODO: use this?
                    textToHighlight={props.englishValue}
                    caseSensitive={false}
                  />
                </View>
                <View style={styles.translationRow}>
                  <Text style={styles.locale}>{props.locale.iso_639_3}:</Text>
                  <Highlighter
                    highlightStyle={styles.highlightedText}
                    searchWords={props.searchTerms}
                    autoEscape={true}// @TODO: use this?
                    textToHighlight={props.localeValue}
                    caseSensitive={false}
                  />
                  {isAdmin &&
                    <IconButton
                      icon='pencil'
                      size={18}
                      iconColor={styles.colors.gray}
                      onPress={(event) => {
                        event.stopPropagation();
                        setUpdatedTextValue(props.localeValue);
                        setIsEditable(true);
                      }}
                    />
                  }
                </View>
              </React.Fragment>
            )
          }
        </TouchableOpacity>

        <View style={styles.buttons}>
          {/* @TODO: if rodowd, show vote counts */}
          {!props.isGeneralFeedback &&
            <React.Fragment>
              <IconButton
                icon={thumbUpIcon}
                iconColor={thumbUpIconColor}
                onPress={() => {
                  dispatch(addI18nNote(props.i18nId, 1));
                  // @TODO: updates to translations are going to have to be new rows in db to prevent losing data
                }}
              />
              <IconButton
                icon={thumbDownIcon}
                iconColor={thumbDownIconColor}
                onPress={() => {
                  dispatch(addI18nNote(props.i18nId, 0));
                  setIsNotesOpen(true);
                }}
              />
            </React.Fragment>
          }
          <IconButton
            icon='message-bulleted'
            iconColor={styles.colors.primaryLight}
            onPress={() => setIsNotesOpen(!_isNotesOpen)}
          />{/* @TODO: add badge for how many notes are there */}
          {/* @TODO: if rodowd, ability to edit in place or replace with new text */}
          {/* @TODO: if rodowd, ability to disable (for ones that aren't used anymore) */}
          {/* @TODO: if props.status !== 'unmarked': option to change vote */}
          {/* @TODO: if props.status === 'incorrect': option to change explanation text */}
        </View>
      </View>

      {_isNotesOpen &&
        <View style={styles.notesWrapper}>
          {notes.filter((n) => n.notes).map((n) => {
            return <Note key={n.id} {...n} />;
          })}
          <View style={styles.newNoteWrapper}>
            <TextInput
              value={_newNoteText}
              mode='outlined'
              onChangeText={(text) => setNewNoteText(text)}
              onKeyPress={(e: any) => { // @TODO: this used to be onKeyDown. switched to paper, and now this needs to be tested...not sure this works anymore
                if (e.code === 'Enter' && e.metaKey) {
                  _saveNote();
                }
              }}
              multiline={true}
              label='New note'// @TODO: i18n
            />
            <View style={styles.saveButtonWrapper}>{/* @TODO: should this be inside of the text input? */}
              <IconButton
                icon='send'
                iconColor={_newNoteText ? styles.colors.primary : styles.colors.lightGray}
                disabled={!_newNoteText}
                onPress={_saveNote}
              />
            </View>
          </View>
        </View>
      }

      {isAdmin && _isEditable &&
        <Dialog
          visible={true}
          onDismiss={() => setIsEditable(false)}
        >
          <Dialog.Title>Update translation</Dialog.Title>{/* @TODO: i18n */}
          <Dialog.Content style={styles.updateDialogContent}>
            <View style={styles.translationRow}>
              <Text style={styles.locale}>key:</Text>
              <Text>{props.i18nKey}</Text>
            </View>
            <View style={styles.translationRow}>
              <Text style={styles.locale}>eng:</Text>
              <Text>{props.englishValue}</Text>
            </View>
            <View style={styles.translationRow}>
              <Text style={styles.locale}>{props.locale.iso_639_3}:</Text>
              <Text>{props.localeValue}</Text>
            </View>
            <TextInput
              value={_updatedTextValue}
              mode='outlined'
              onChangeText={(text) => setUpdatedTextValue(text)}
              // @TODO: multiline={true}// @TODO: i like multiline true, but it should default to 1 line unless it needs to expand
              label='Updated value'// @TODO: i18n
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => setIsEditable(false)}
            >
              {i18n('cancel')}
            </Button>
            <Button
              disabled={props.localeValue === _updatedTextValue}
              onPress={() => {
                dispatch(updateI18nValue(props.i18nId, _updatedTextValue));
                setIsEditable(false);
                setIsNotesOpen(true);
              }}
            >
              {i18n('save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </View>
  );
};

class TranslationProps {
  englishValue: string | any; // @TODO: remove func and object when it's on back end?
  i18nId: number;
  i18nKey: string;
  mostRecentVote: number;
  locale: any;
  localeValue: string | any; // @TODO: remove func and object when it's on back end?
  searchTerms: string[];

  isGeneralFeedback?: boolean;
  status?: 'correct' | 'incorrect' | 'unmarked';
};

export default Translation;
